/**
 * useCachedLorPreview
 *
 * A hook that provides optimized PDF preview for LOR documents.
 * Prefers the cached PDF URL if available, falling back to the
 * preview endpoint which renders and caches on-demand.
 */
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useEffect, useState } from 'react';

import { useGetMatterDocumentPreviewQuery } from '@/services/api/mattersService';

import type { MatterDocLite } from '../../types';

interface UseCachedLorPreviewOptions {
  /** The matter ID for the document */
  matterId: string;
  /** The document instance (may have cached_pdf_url) */
  documentInstance: MatterDocLite | null;
  /** Whether to automatically fetch if no cached URL */
  autoFetch?: boolean;
}

interface UseCachedLorPreviewResult {
  /** Object URL for the PDF, ready for display */
  url: string | null;
  /** The filename for download purposes */
  filename: string;
  /** Whether the preview is currently loading */
  isLoading: boolean;
  /** Any error that occurred */
  error: unknown;
  /** Manually trigger a fresh preview (bypasses cache) */
  refresh: () => void;
  /** Whether the URL came from the cached file vs fresh render */
  isCached: boolean;
}

/**
 * Hook for optimized LOR PDF preview with caching support.
 *
 * @param options Configuration options
 * @returns Preview state and controls
 */
export const useCachedLorPreview = ({
  matterId,
  documentInstance,
  autoFetch = true,
}: UseCachedLorPreviewOptions): UseCachedLorPreviewResult => {
  const [url, setUrl] = useState<string | null>(null);
  const [isCached, setIsCached] = useState(false);
  const [isRequested, setIsRequested] = useState(false);
  const [nonce, setNonce] = useState<number | null>(null);
  const [filename, setFilename] = useState<string>(
    'Letter-of-Representation.pdf',
  );

  const documentId = documentInstance?.id ?? null;
  const cachedPdfUrl = documentInstance?.cached_pdf_url ?? null;

  // Track the previous document ID to detect changes
  const [prevDocumentId, setPrevDocumentId] = useState<string | null>(null);

  // Effect: Reset state when document changes
  useEffect(() => {
    if (documentId !== prevDocumentId) {
      // Document changed - reset state
      if (url && !isCached) {
        URL.revokeObjectURL(url);
      }
      setUrl(null);
      setIsCached(false);
      setIsRequested(false);
      setPrevDocumentId(documentId);
    }
  }, [documentId, prevDocumentId, url, isCached]);

  // Only query the backend if we don't have a cached URL and auto-fetch is enabled
  const shouldFetchFromBackend =
    !cachedPdfUrl && documentId && autoFetch && isRequested;

  const {
    data: previewData,
    isFetching,
    error,
  } = useGetMatterDocumentPreviewQuery(
    shouldFetchFromBackend
      ? { matter_id: matterId, document_id: documentId, t: nonce ?? undefined }
      : skipToken,
  );

  // Effect: Use cached URL if available
  useEffect(() => {
    if (cachedPdfUrl) {
      // Use the cached URL directly - it's already a valid URL
      setUrl(cachedPdfUrl);
      setIsCached(true);
      setIsRequested(false);

      // Extract filename from URL if possible
      try {
        const urlObj = new URL(cachedPdfUrl, window.location.origin);
        const pathParts = urlObj.pathname.split('/');
        const lastPart = pathParts[pathParts.length - 1];
        if (lastPart && lastPart.endsWith('.pdf')) {
          setFilename(decodeURIComponent(lastPart));
        }
      } catch {
        // Keep default filename
      }
    }
  }, [cachedPdfUrl]);

  // Effect: Handle preview data from backend (when no cached URL)
  useEffect(() => {
    if (!previewData) {
      return () => {};
    }

    const nextUrl = URL.createObjectURL(previewData.blob);
    setUrl(nextUrl);
    setIsCached(false);
    setFilename(previewData.filename || 'Letter-of-Representation.pdf');
    setIsRequested(false);

    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [previewData]);

  // Effect: Auto-request if we have a document but no cached URL
  useEffect(() => {
    // Only auto-request if:
    // - We have a document ID
    // - No cached URL available
    // - Auto-fetch is enabled
    // - Not already requested
    // - No URL yet (or document just changed)
    if (
      documentId &&
      documentId === prevDocumentId &&
      !cachedPdfUrl &&
      autoFetch &&
      !isRequested &&
      !url
    ) {
      setIsRequested(true);
      setNonce(Date.now());
    }
  }, [documentId, prevDocumentId, cachedPdfUrl, autoFetch, isRequested, url]);

  // Effect: Reset on error
  useEffect(() => {
    if (error) {
      setIsRequested(false);
    }
  }, [error]);

  // Refresh: Force a fresh fetch (bypasses cached URL)
  const refresh = useCallback(() => {
    // Revoke any existing object URL
    if (url && !isCached) {
      URL.revokeObjectURL(url);
    }

    setUrl(null);
    setIsCached(false);
    setIsRequested(true);
    setNonce(Date.now());
  }, [url, isCached]);

  const isLoading =
    isFetching || (isRequested && !error && !url && !cachedPdfUrl);

  return {
    url,
    filename,
    isLoading,
    error,
    refresh,
    isCached,
  };
};
